import { Call, Device } from '@twilio/voice-sdk';

export type ManualCallUiState =
  | 'idle'
  | 'registering'
  | 'ready'
  | 'connecting'
  | 'connected'
  | 'disconnecting'
  | 'error';

export type ManualCallState = {
  status: ManualCallUiState;
  prospectId: string | null;
  callId: string | null;
  error: string | null;
};

type StartManualCallArgs = {
  authToken: string;
  prospectId: string;
  callId: string;
  callSheetId: string;
  toNumber: string;
};

type Listener = (state: ManualCallState) => void;

class TwilioVoiceService {
  private device: Device | null = null;
  private activeCall: Call | null = null;
  private listeners = new Set<Listener>();
  private lastAuthToken: string | null = null;
  private state: ManualCallState = {
    status: 'idle',
    prospectId: null,
    callId: null,
    error: null
  };

  private emit(next: Partial<ManualCallState>) {
    this.state = { ...this.state, ...next };
    this.listeners.forEach((listener) => listener(this.state));
  }

  private async fetchVoiceToken(authToken: string): Promise<string> {
    const resp = await fetch('/api/twilio-voice-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`
      }
    });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || !data?.token) {
      throw new Error(String(data?.error || 'Failed to initialize browser calling.'));
    }
    return String(data.token);
  }

  private wireDevice(device: Device) {
    device.on('tokenWillExpire', async () => {
      if (!this.lastAuthToken) return;
      try {
        const token = await this.fetchVoiceToken(this.lastAuthToken);
        device.updateToken(token);
      } catch (error: any) {
        this.emit({
          status: 'error',
          error: String(error?.message || error || 'Browser calling token refresh failed.')
        });
      }
    });

    device.on('error', (error) => {
      this.emit({
        status: 'error',
        error: String((error as any)?.message || error || 'Browser calling error.')
      });
    });
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(): ManualCallState {
    return this.state;
  }

  async ensureReady(authToken: string): Promise<void> {
    this.lastAuthToken = authToken;
    if (this.device) {
      if (this.state.status === 'idle') this.emit({ status: 'ready', error: null });
      return;
    }

    this.emit({ status: 'registering', error: null });
    const token = await this.fetchVoiceToken(authToken);
    const device = new Device(token, {
      logLevel: 1
    });
    this.wireDevice(device);

    await new Promise<void>((resolve, reject) => {
      const cleanup = () => {
        device.off('registered', onRegistered);
        device.off('error', onError);
      };
      const onRegistered = () => {
        cleanup();
        resolve();
      };
      const onError = (error: any) => {
        cleanup();
        reject(error);
      };
      device.on('registered', onRegistered);
      device.on('error', onError);
      void device.register();
    });

    this.device = device;
    this.emit({ status: 'ready', error: null });
  }

  async startCall(args: StartManualCallArgs): Promise<void> {
    await this.ensureReady(args.authToken);
    if (!this.device) throw new Error('Browser calling device not ready.');
    if (this.activeCall) throw new Error('Another browser call is already active.');

    this.emit({
      status: 'connecting',
      prospectId: args.prospectId,
      callId: args.callId,
      error: null
    });

    const call = await this.device.connect({
      params: {
        to: args.toNumber,
        prospectId: args.prospectId,
        callId: args.callId,
        callSheetId: args.callSheetId
      }
    });

    this.activeCall = call;

    call.on('accept', () => {
      this.emit({ status: 'connected', error: null });
    });
    call.on('disconnect', () => {
      this.activeCall = null;
      this.emit({
        status: this.device ? 'ready' : 'idle',
        prospectId: null,
        callId: null,
        error: null
      });
    });
    call.on('cancel', () => {
      this.activeCall = null;
      this.emit({
        status: this.device ? 'ready' : 'idle',
        prospectId: null,
        callId: null,
        error: null
      });
    });
    call.on('reject', () => {
      this.activeCall = null;
      this.emit({
        status: this.device ? 'ready' : 'idle',
        prospectId: null,
        callId: null,
        error: null
      });
    });
    call.on('error', (error) => {
      this.activeCall = null;
      this.emit({
        status: 'error',
        prospectId: null,
        callId: null,
        error: String((error as any)?.message || error || 'Browser call failed.')
      });
    });
  }

  async endCall(): Promise<void> {
    if (!this.activeCall) return;
    this.emit({ status: 'disconnecting', error: null });
    this.activeCall.disconnect();
  }
}

const twilioVoiceService = new TwilioVoiceService();

export default twilioVoiceService;
